import api from "../api";
import type {
  ReviewComment,
  CreateReviewCommentDto,
  ReplyReviewCommentDto,
} from "../models/ReviewComment";

type ApiResponse<T> = {
  message: string;
  data: T | null;
  statusCode: number;
  error: string | null;
};

function stripNullish<T extends object>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj as Record<string, unknown>).filter(
      ([, value]) => value !== null && value !== undefined
    )
  ) as Partial<T>;
}

function normalizeOem(oem: string): string {
  const raw = String(oem || "").trim().replace(/^\/+/, "");
  if (!raw) return "Oem1";

  const match = raw.match(/^oem(\d+)$/i);
  if (match) return `Oem${match[1]}`;

  return raw;
}

function isSuccess(statusCode?: number): boolean {
  return !!statusCode && statusCode >= 200 && statusCode < 300;
}

function toApiError<T>(
  response: ApiResponse<T> | undefined,
  fallbackMessage: string
): Error {
  return new Error(response?.error || response?.message || fallbackMessage);
}

export async function fetchReviewComments(
  oem: string,
  masterAssemblyId: number
): Promise<ReviewComment[]> {
  const normalizedOem = normalizeOem(oem);
  const { data } = await api.get<ApiResponse<ReviewComment[]>>(
    `/ReviewComments/${normalizedOem}/${masterAssemblyId}`
  );

  if (!isSuccess(data?.statusCode)) {
    throw toApiError(data, "Failed to load review comments");
  }

  return Array.isArray(data.data) ? data.data : [];
}

export async function createReviewComment(
  oem: string,
  payload: CreateReviewCommentDto
): Promise<ReviewComment> {
  const normalizedOem = normalizeOem(oem);
  const cleanedPayload = stripNullish(payload);
  const { data } = await api.post<ApiResponse<ReviewComment>>(
    `/ReviewComments/${normalizedOem}`,
    cleanedPayload
  );

  if (isSuccess(data?.statusCode) && data.data) {
    return data.data;
  }

  throw toApiError(data, "Failed to create review comment");
}

export async function replyToReviewComment(
  oem: string,
  payload: ReplyReviewCommentDto
): Promise<ReviewComment> {
  const normalizedOem = normalizeOem(oem);
  const cleanedPayload = stripNullish(payload);
  const { data } = await api.post<ApiResponse<ReviewComment>>(
    `/ReviewComments/${normalizedOem}/reply`,
    cleanedPayload
  );

  if (isSuccess(data?.statusCode) && data.data) {
    return data.data;
  }

  throw toApiError(data, "Failed to reply to review comment");
}

export async function resolveReviewThread(
  oem: string,
  threadId: string
): Promise<ReviewComment> {
  const normalizedOem = normalizeOem(oem);
  const { data } = await api.put<ApiResponse<ReviewComment>>(
    `/ReviewComments/${normalizedOem}/resolve/${threadId}`
  );

  if (isSuccess(data?.statusCode) && data.data) {
    return data.data;
  }

  throw toApiError(data, "Failed to resolve review thread");
}

export async function reopenReviewThread(
  oem: string,
  threadId: string
): Promise<ReviewComment> {
  const normalizedOem = normalizeOem(oem);
  const { data } = await api.put<ApiResponse<ReviewComment>>(
    `/ReviewComments/${normalizedOem}/reopen/${threadId}`
  );

  if (isSuccess(data?.statusCode) && data.data) {
    return data.data;
  }

  throw toApiError(data, "Failed to reopen review thread");
}
